// SPDX-License-Identifier: AGPL-3.0-or-later

import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {Combobox} from '@app/features/ui/components/form/FormCombobox';
import {Textarea} from '@app/features/ui/components/form/FormInput';
import styles from '@app/features/user/components/modals/tabs/applications_tab/application_detail/ApplicationDetail.module.css';
import type {
	ApplicationDetailForm,
	BotBioLocalization,
} from '@app/features/user/components/modals/tabs/applications_tab/application_detail/ApplicationDetailTypes';
import {getCurrentLocale, getSortedLocales} from '@app/features/user/utils/LocaleUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {XIcon} from '@phosphor-icons/react';
import {Controller} from 'react-hook-form';

const ADD_LANGUAGE_DESCRIPTOR = msg({
	message: 'Add a language',
	comment: 'Placeholder of the picker that adds a translated bot bio, in the bot profile section.',
});
const BIO_IN_LANGUAGE_DESCRIPTOR = msg({
	message: 'Bot bio ({language})',
	comment: 'Label of a translated bot bio in the bot profile section. {language} is a language name.',
});
const REMOVE_LANGUAGE_DESCRIPTOR = msg({
	message: 'Remove {language}',
	comment: 'Accessible label of the button that removes a translated bot bio. {language} is a language name.',
});
const TRANSLATIONS_HINT_DESCRIPTOR = msg({
	message: 'People whose app is in one of these languages see that bio; everyone else sees the bio above.',
	comment: 'Help text under the translated bot bios in the bot profile section.',
});

const BIO_MAX_LENGTH = 1024;

export const BotBioLocalizationsField = ({form}: {form: ApplicationDetailForm}) => {
	const {i18n} = useLingui();
	const locales = getSortedLocales().sort((a, b) => a.name.localeCompare(b.name, getCurrentLocale()));
	const nameOf = (code: string) => locales.find((locale) => locale.code === code)?.name ?? code;
	return (
		<Controller
			name="bioLocalizations"
			control={form.control}
			render={({field}) => {
				const entries: Array<BotBioLocalization> = field.value ?? [];
				const used = new Set(entries.map((entry) => entry.locale));
				const update = (next: Array<BotBioLocalization>) => field.onChange(next);
				return (
					<div className={styles.redirectList} data-flx="user.applications-tab.bot-bio-localizations.list">
						{entries.map((entry, index) => (
							<div
								key={entry.locale}
								className={styles.localizedBio}
								data-flx="user.applications-tab.bot-bio-localizations.row"
							>
								<Textarea
									label={i18n._(BIO_IN_LANGUAGE_DESCRIPTOR, {language: nameOf(entry.locale)})}
									value={entry.text}
									onChange={(event) =>
										update(entries.map((item, i) => (i === index ? {...item, text: event.target.value} : item)))
									}
									minRows={2}
									maxRows={6}
									maxLength={BIO_MAX_LENGTH}
									data-flx="user.applications-tab.bot-bio-localizations.textarea"
								/>
								<button
									type="button"
									className={styles.localizedBioRemove}
									onClick={() => update(entries.filter((_, i) => i !== index))}
									aria-label={i18n._(REMOVE_LANGUAGE_DESCRIPTOR, {language: nameOf(entry.locale)})}
									data-flx="user.applications-tab.bot-bio-localizations.remove"
								>
									<XIcon size={remFromPx(18)} weight="bold" />
								</button>
							</div>
						))}
						<Combobox<string>
							value=""
							placeholder={i18n._(ADD_LANGUAGE_DESCRIPTOR)}
							options={locales
								.filter((locale) => !used.has(locale.code))
								.map((locale) => ({value: locale.code, label: locale.name}))}
							onChange={(code) => code && update([...entries, {locale: code, text: ''}])}
							isSearchable
							data-flx="user.applications-tab.bot-bio-localizations.add"
						/>
						{entries.length > 0 && <p className={styles.toggleDescription}>{i18n._(TRANSLATIONS_HINT_DESCRIPTOR)}</p>}
					</div>
				);
			}}
		/>
	);
};
